import { plainToInstance } from 'class-transformer'

import { CREATE_BOOK_COLLECTION } from '@client/collections/BookCollection/schemas/createBookCollection'
import { FIND_BOOK_COLLECTION } from '@client/collections/BookCollection/schemas/findBookCollection'
import { FIND_BOOK_COLLECTION_DETAIL } from '@client/collections/BookCollection/schemas/findBookCollectionDetail'
import { GET_BOOK_COLLECTION_BY_USER_ID } from '@client/collections/BookCollection/schemas/getBookCollectionByUserId'
import { GET_MY_BOOK_COLLECTION } from '@client/collections/BookCollection/schemas/getMyBookCollection'
import { REMOVE_BOOK_COLLECTION } from '@client/collections/BookCollection/schemas/removeBookCollection'
import { TOGGLE_BOOK_COLLECTION_LIKE } from '@client/collections/BookCollection/schemas/toggleBookCollectionLike'
import { UPDATE_BOOK_COLLECTION } from '@client/collections/BookCollection/schemas/updateBookCollection'
import { BookCollectionPrivacyEnum } from '@interfaces/BookCollectionPrivacyEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { BookCollectionDetailResponse } from '@models/bookCollection/BookCollectionDetailResponse'
import { BookCollectionFormType } from '@models/bookCollection/BookCollectionFormType'
import { BookCollectionLikeStatusEnum } from '@models/bookCollection/BookCollectionLikeStatusEnum'
import { BookCollectionType } from '@models/bookCollection/BookCollectionType'
import { MyBookType } from '@models/bookCollection/MyBookType'
import { gqlApiInstance } from '@client/init'

export function useBookCollectionAction() {
  async function getMyBooks(params: {
    searchText: string
    bookType: BookEnum | null
    category: number | null
  }): Promise<MyBookType[]> {
    const { getMyBookCollection } = await gqlApiInstance.request(
      GET_MY_BOOK_COLLECTION,
      params
    )
    return plainToInstance(
      MyBookType,
      <any[]>getMyBookCollection?.map((item: any) => item.book)
    )
  }

  async function createBookCollection(
    form: BookCollectionFormType
  ): Promise<void> {
    await gqlApiInstance.request(CREATE_BOOK_COLLECTION, {
      createBookCollectionInput: {
        ...form,
        books: form.books.map(item => item.id),
      },
    })
  }

  async function getBookCollectionDetail({
    id,
    page,
    perpage,
  }: {
    id: number
    page: number
    perpage: number
  }): Promise<BookCollectionDetailResponse> {
    const { findBookCollectionDetail } = await gqlApiInstance.request(
      FIND_BOOK_COLLECTION_DETAIL,
      {
        id,
        page,
        limitPerPage: perpage,
      }
    )

    return plainToInstance(
      BookCollectionDetailResponse,
      findBookCollectionDetail
    )
  }

  async function getBookCollections({
    privacyType,
  }: {
    privacyType: BookCollectionPrivacyEnum | null
  }): Promise<BookCollectionType[]> {
    const { findBookCollection } = await gqlApiInstance.request(
      FIND_BOOK_COLLECTION,
      { privacyType }
    )
    return plainToInstance(BookCollectionType, <any[]>findBookCollection)
  }

  async function updateBookCollection(
    form: BookCollectionFormType
  ): Promise<void> {
    await gqlApiInstance.request(UPDATE_BOOK_COLLECTION, {
      updateBookCollectionInput: {
        ...form,
        books: form.books.map(item => item.id),
      },
    })
  }

  async function removeBookCollection(id: number): Promise<{ id: number }> {
    const res = await gqlApiInstance.request(REMOVE_BOOK_COLLECTION, {
      removeBookCollectionId: id,
    })

    return res.removeBookCollection
  }

  async function toggleBookCollectionLike(
    bookCollectionId: number
  ): Promise<BookCollectionLikeStatusEnum> {
    const res = await gqlApiInstance.request(TOGGLE_BOOK_COLLECTION_LIKE, {
      input: {
        bookCollectionId,
      },
    })

    return res.toggleBookCollectionLike.type
  }

  async function getBookCollectionByUserId(
    userId: number
  ): Promise<BookCollectionType[]> {
    const { getBookCollectionsByUserId } = await gqlApiInstance.request(
      GET_BOOK_COLLECTION_BY_USER_ID,
      {
        privacyType: BookCollectionPrivacyEnum.PUBLIC,
        userId,
      }
    )

    return plainToInstance(
      BookCollectionType,
      <any[]>getBookCollectionsByUserId
    )
  }

  return {
    getMyBooks,
    createBookCollection,
    getBookCollectionDetail,
    getBookCollections,
    updateBookCollection,
    removeBookCollection,
    toggleBookCollectionLike,
    getBookCollectionByUserId,
  }
}
